import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useLocation } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useSession } from "../../../configuration/session/hooks/useSession";
import { useStudent } from "../../../peoples/students/hooks/useStudent";
import { useMarks } from "../hooks/useMarks";
import { showToast } from "../../../../core/utils/alert";
import { MarksPayloadType } from "../types/marksRequest";
import { useSelector } from "react-redux";
import {
  selectUserRole,
  selectCurrentUser,
} from "../../../../core/data/slice/authSlice";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";

interface CourseStage {
  id: number;
  name: string;
  max_marks: number;
  min_marks: number;
}

const AssignMarks = () => {
  const routes = all_routes;
  const location = useLocation();

  // Custom styles
  const styles = {
    marksInputContainer: {
      width: "120px",
    },
    bulkAssignmentInput: {
      width: "100px",
      height: "38px",
    },
    bulkAssignmentCard: {
      borderLeft: "4px solid #3d5ee1",
    },
  };

  // Add state for filters
  const [selectedSession, setSelectedSession] = useState<string>("");
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedCenterId, setSelectedCenterId] = useState<string>("");
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  // Get user role to determine if center filter should be shown
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";
  const isCenterAdmin = userRole === "centeradmin";
  const currentUser = useSelector(selectCurrentUser);

  // Get data from hooks with pagination
  const { getCourseState } = useCourse({ published: true });
  const { getSessionState } = useSession();

  // Only get centers if the user is a super admin
  const { getCenterState } = useCenter({ skip: !isSuperAdmin });
  const { centers } = isSuperAdmin ? getCenterState() : { centers: [] };

  const { getStudentState, paginationData } = useStudent({
    page: currentPage,
    size: recordsPerPage,
    search: searchText,
    course_id: selectedCourseId || undefined,
    stage_id: selectedLevel ? Number(selectedLevel) : undefined,
    session_id: selectedSession ? Number(selectedSession) : undefined,
    ...(isSuperAdmin && selectedCenterId
      ? { center_id: Number(selectedCenterId) }
      : {}),
    status: "active",
    marks: true, // Make sure to request marks with student data
  });

  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { sessions, isLoading: isLoadingSessions } = getSessionState();
  const { students, isLoading: isLoadingStudents } = getStudentState();

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [courseStages, setCourseStages] = useState<CourseStage[]>([]);
  const [marks, setMarks] = useState<{ [key: number]: string }>({});
  const [assignToSelectedMarks, setAssignToSelectedMarks] =
    useState<string>("");
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

  const {
    saveMarks,
    updateMarks,
    saveBulkMarks,
    updateBulkMarksData,
    isAssigningBulk,
    isUpdatingBulk,
  } = useMarks({
    students: students,
    session_id: selectedSession,
    course_id: selectedCourseId?.toString(),
    stage_id: selectedLevel,
  });

  // Add this state to track which student's marks are being updated
  const [loadingStudentId, setLoadingStudentId] = useState<number | null>(null);

  // Add a ref to store newly added marks that aren't yet in the students array
  const newlyAddedMarks = React.useRef<{ [key: number]: { id: number } }>({});

  // Transform student data for table
  const tableData = useMemo(() => {
    return students.map((student) => {
      const currentStage = courseStages.find(
        (stage) => stage.id === Number(selectedLevel)
      );

      // Find existing mark for this student in the current stage/course/session
      const existingMark = student.marks?.find(
        (mark) =>
          mark.stage?.id === Number(selectedLevel) &&
          mark.course?.id === selectedCourseId
      );

      return {
        id: student.id,
        studentId: student.profile.studentid,
        name: `${student.first_name} ${student.last_name}`,
        courseName: student.course?.name || "-",
        stageName: student.course?.stage?.name || "-",
        email: student.profile.email,
        phone: student.profile.phone,
        max: currentStage?.max_marks || 0,
        min: currentStage?.min_marks || 0,
        studentNumericId: student.id,
        existingMark: existingMark,
      };
    });
  }, [students, courseStages, selectedLevel, selectedCourseId]);

  // Pagination handler
  const handlePaginationChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    if (pageSize) setRecordsPerPage(pageSize);
  };

  // Search handler
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleMarkChange = (studentId: number, value: string) => {
    // If empty string, allow it
    if (value === "") {
      setMarks((prev) => ({
        ...prev,
        [studentId]: value,
      }));
      return;
    }

    // Get max marks for the current stage
    const maxMarks =
      courseStages.find((stage) => stage.id === Number(selectedLevel))
        ?.max_marks || 0;

    // Convert to number and validate against stage max_marks
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue <= maxMarks) {
      setMarks((prev) => ({
        ...prev,
        [studentId]: value,
      }));
    }
  };

  useEffect(() => {
    setIsEdit(location.pathname === routes.editCenter);
  }, [location.pathname]);

  // Add useEffect to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    // Clear any newly added marks when filters change
    newlyAddedMarks.current = {};
  }, [selectedCourseId, selectedLevel, selectedSession]);

  // Update handleCourseChange
  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const courseId = Number(e.target.value);
    setSelectedCourseId(courseId);
    setSelectedLevel(""); // Reset level when course changes
    setCourseStages(
      courses.find((course) => course.id === courseId)?.courseStages || []
    );
    setMarks({}); // Reset marks when course changes
    setSelectedStudentIds([]); // Reset selected students
  };

  // Update session change handler
  const handleSessionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSession(e.target.value);
    if (isSuperAdmin) {
      setSelectedCenterId(""); // Reset center when session changes
    }
    setSelectedCourseId(null); // Reset course when session changes
    setSelectedLevel(""); // Reset level when session changes
    setMarks({}); // Reset marks when session changes
    setSelectedStudentIds([]); // Reset selected students
  };

  // Update level change handler
  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLevel(e.target.value);
    setMarks({}); // Reset marks when level changes
    setSelectedStudentIds([]); // Reset selected students
  };

  // Add center change handler
  const handleCenterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCenterId(e.target.value);
    setSelectedCourseId(null); // Reset course when center changes
    setSelectedLevel(""); // Reset level when center changes
    setMarks({}); // Reset marks when center changes
    setSelectedStudentIds([]); // Reset selected students
  };

  const handleAssignToSelectedMarks = (value: string) => {
    const markValue = parseInt(value);
    if (!isNaN(markValue)) {
      if (markValue >= 0 && markValue <= 100) {
        setAssignToSelectedMarks(value);
      }
    } else if (value === "") {
      setAssignToSelectedMarks("");
    }
  };

  const handleAssignToSelected = () => {
    const markValue = parseInt(assignToSelectedMarks);
    if (!isNaN(markValue) && markValue >= 0 && markValue <= 100) {
      const newMarks = { ...marks };
      selectedStudentIds.forEach((studentId) => {
        newMarks[studentId] = markValue.toString();
      });
      setMarks(newMarks);
    }
  };

  const handleSaveIndividual = async (studentId: number) => {
    if (
      !selectedCourseId ||
      !selectedLevel ||
      !selectedSession ||
      (isSuperAdmin && !selectedCenterId)
    ) {
      showToast({
        title: "Error",
        text: "Please select all required filters",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    const markValue = marks[studentId];
    if (!markValue || markValue === "") {
      showToast({
        title: "Error",
        text: "Please enter a valid mark",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    setLoadingStudentId(studentId);

    try {
      const existingMark = doesMarkExist(studentId);

      const markPayload = {
        student_id: studentId,
        course_id: selectedCourseId,
        stage_id: Number(selectedLevel),
        session_id: Number(selectedSession),
        obtained: Number(markValue),
        ...(isSuperAdmin && selectedCenterId
          ? { center_id: Number(selectedCenterId) }
          : isCenterAdmin && currentUser?.center_id
          ? { center_id: Number(currentUser.center_id) }
          : {}),
      };

      let result;
      if (existingMark && typeof existingMark.id === "number") {
        // Only update if we have a valid mark ID
        result = await updateMarks(existingMark.id, markPayload);
      } else {
        // If mark ID is invalid or doesn't exist, use save API
        result = await saveMarks(markPayload);

        // If saving was successful, add to our ref to track newly added marks
        if (result.success && result.data) {
          // Store the actual mark ID from the API response
          const markId = result.data.id;

          // Only store if we have a valid ID
          if (markId && typeof markId === "number") {
            newlyAddedMarks.current[studentId] = { id: markId };

            // Force a re-render to update the UI
            setMarks({ ...marks });
          }
        }
      }

      if (result.success) {
        showToast({
          title: "Success",
          text: existingMark
            ? "Mark updated successfully!"
            : "Mark saved successfully!",
          icon: "success",
          timer: 5000,
        });
      } else {
        showToast({
          title: "Error",
          text: `Error ${existingMark ? "updating" : "saving"} mark: ${
            result.error
          }`,
          icon: "error",
          timer: 5000,
        });
      }
    } catch (error) {
      showToast({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
        timer: 5000,
      });
    } finally {
      setLoadingStudentId(null);
    }
  };

  const handleSaveAll = async () => {
    if (
      !selectedCourseId ||
      !selectedLevel ||
      !selectedSession ||
      (isSuperAdmin && !selectedCenterId)
    ) {
      showToast({
        title: "Error",
        text: "Please select all required filters",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    const marksToUpdate: Array<{
      id: number;
      student_id: number;
      course_id: number;
      stage_id: number;
      session_id: number;
      obtained: number;
      center_id?: number;
    }> = [];

    const marksToSave: MarksPayloadType[] = [];
    const newSaveIds: number[] = [];

    // Get the IDs of students on the current page
    const currentPageStudentIds = tableData.map((student) => student.id);

    // Count how many empty inputs we have
    const emptyInputsCount = tableData.filter(
      (student) => !marks[student.id] || marks[student.id] === ""
    ).length;

    // Only process students where marks have been entered in the input fields
    // AND only include students from the current page
    Object.entries(marks)
      .filter(
        ([studentId, mark]) =>
          // Only include non-empty marks
          mark !== "" &&
          !isNaN(Number(mark)) &&
          // Only include students from the current page
          currentPageStudentIds.includes(Number(studentId))
      )
      .forEach(([studentId, mark]) => {
        const existingMark = doesMarkExist(Number(studentId));

        const markPayload = {
          student_id: Number(studentId),
          course_id: selectedCourseId,
          stage_id: Number(selectedLevel),
          session_id: Number(selectedSession),
          obtained: Number(mark),
          ...(isSuperAdmin && selectedCenterId
            ? { center_id: Number(selectedCenterId) }
            : isCenterAdmin && currentUser?.center_id
            ? { center_id: Number(currentUser.center_id) }
            : {}),
        };

        if (
          existingMark &&
          existingMark.id &&
          typeof existingMark.id === "number"
        ) {
          // Only add to update array if we have a valid mark ID
          marksToUpdate.push({
            id: existingMark.id,
            ...markPayload,
          });
        } else {
          marksToSave.push(markPayload);
          newSaveIds.push(Number(studentId));
        }
      });

    // Check if we have any marks to process
    if (marksToUpdate.length === 0 && marksToSave.length === 0) {
      showToast({
        title: "Info",
        text: "No marks entered. Please enter marks before saving.",
        icon: "info",
        timer: 5000,
      });
      return;
    }

    let hasError = false;
    let updateSuccess = false;
    let saveSuccess = false;
    let updatedCount = 0;
    let savedCount = 0;

    // Handle updates - use individual or bulk API based on count and empty inputs
    if (marksToUpdate.length > 0) {
      if (marksToUpdate.length === 1 && emptyInputsCount === 0) {
        // Use individual update API for single mark when there are no empty inputs
        const markToUpdate = marksToUpdate[0];
        const updateResult = await updateMarks(markToUpdate.id, {
          student_id: markToUpdate.student_id,
          course_id: markToUpdate.course_id,
          stage_id: markToUpdate.stage_id,
          session_id: markToUpdate.session_id,
          obtained: markToUpdate.obtained,
        });

        if (!updateResult.success) {
          showToast({
            title: "Error",
            text: `Error updating mark: ${updateResult.error}`,
            icon: "error",
            timer: 5000,
          });
          hasError = true;
        } else {
          updateSuccess = true;
          updatedCount = 1;
        }
      } else {
        // Use bulk update API for multiple marks or when there are empty inputs
        const updateResult = await updateBulkMarksData({
          marks: marksToUpdate,
        });
        if (!updateResult.success) {
          showToast({
            title: "Error",
            text: `Error updating marks: ${updateResult.error}`,
            icon: "error",
            timer: 5000,
          });
          hasError = true;
        } else {
          updateSuccess = true;
          updatedCount = marksToUpdate.length;
        }
      }
    }

    // Handle saves - use individual or bulk API based on count and empty inputs
    if (marksToSave.length > 0) {
      if (marksToSave.length === 1 && emptyInputsCount === 0) {
        // Use individual save API for single mark when there are no empty inputs
        const markToSave = marksToSave[0];
        const saveResult = await saveMarks(markToSave);

        if (!saveResult.success) {
          showToast({
            title: "Error",
            text: `Error saving mark: ${saveResult.error}`,
            icon: "error",
            timer: 5000,
          });
          hasError = true;
        } else if (
          saveResult.data &&
          saveResult.data.id &&
          typeof saveResult.data.id === "number"
        ) {
          // Store the actual mark ID from the API response
          newlyAddedMarks.current[markToSave.student_id] = {
            id: saveResult.data.id,
          };

          // Force a re-render to update the UI
          setMarks({ ...marks });
          saveSuccess = true;
          savedCount = 1;
        }
      } else {
        // Use bulk save API for multiple marks or when there are empty inputs
        const saveResult = await saveBulkMarks({ marks: marksToSave });

        if (!saveResult.success) {
          showToast({
            title: "Error",
            text: `Error saving marks: ${saveResult.error}`,
            icon: "error",
            timer: 5000,
          });
          hasError = true;
        } else {
          // Store the real mark IDs from the response
          if (saveResult.data && Array.isArray(saveResult.data)) {
            // Map student IDs to their corresponding mark IDs from the response
            saveResult.data.forEach((mark, index) => {
              const studentId = marksToSave[index]?.student_id;
              if (studentId && mark && mark.id && typeof mark.id === "number") {
                newlyAddedMarks.current[studentId] = { id: mark.id };
              }
            });
          } else {
            console.error(
              "Invalid response format from bulk save API:",
              saveResult.data
            );
          }

          // Force a re-render to update the UI
          setMarks({ ...marks });
          saveSuccess = true;
          savedCount = marksToSave.length;
        }
      }
    }

    if (!hasError) {
      if (saveSuccess && updateSuccess) {
        showToast({
          title: "Success",
          text: `Successfully saved ${savedCount} mark${
            savedCount > 1 ? "s" : ""
          } and updated ${updatedCount} mark${updatedCount > 1 ? "s" : ""}!`,
          icon: "success",
          timer: 5000,
        });
      } else if (saveSuccess) {
        showToast({
          title: "Success",
          text: `Successfully saved mark${
            savedCount > 1 ? "s" : ""
          } for ${savedCount} student${savedCount > 1 ? "s" : ""}!`,
          icon: "success",
          timer: 5000,
        });
      } else if (updateSuccess) {
        showToast({
          title: "Success",
          text: `Successfully updated mark${
            updatedCount > 1 ? "s" : ""
          } for ${updatedCount} student${updatedCount > 1 ? "s" : ""}!`,
          icon: "success",
          timer: 5000,
        });
      } else {
        // No marks to save or update
        showToast({
          title: "Info",
          text: "No changes to save or update.",
          icon: "info",
          timer: 5000,
        });
      }
    }
  };

  const handleSelectionChange = (selectedKeys: React.Key[]) => {
    setSelectedStudentIds(selectedKeys as number[]);
  };

  // Update the useEffect for pre-filling marks
  useEffect(() => {
    if (students?.length) {
      setMarks((prev) => {
        const newMarks = { ...prev };
        students.forEach((student) => {
          // console.log(student.marks, 'sm--')
          // Find mark for this student in the current stage/course
          const existingMark = student.marks?.find(
            (mark) =>
              mark.stage?.id === Number(selectedLevel) &&
              mark.course?.id === selectedCourseId
          );

          // Only set if not already set by user
          if (existingMark && !(student.id in newMarks)) {
            newMarks[student.id] = existingMark?.stage?.obtained?.toString() || "";
          }
        });
        return newMarks;
      });
    }
  }, [students, selectedLevel, selectedCourseId]);

  const doesMarkExist = (studentId: number) => {
    // First check if we have a newly added mark for this student
    if (newlyAddedMarks.current[studentId]) {
      // Return the stored mark info with the real ID
      return newlyAddedMarks.current[studentId];
    }

    const student = students.find((s) => s.id === studentId);
    if (!student || !student.marks?.length) return null;

    // Find mark for this student in the current stage/course
    const existingMark = student.marks.find(
      (mark) =>
        mark.stage?.id === Number(selectedLevel) &&
        mark.course?.id === selectedCourseId
    );

    return existingMark;
  };

  // Update the getResult function to use min marks from the API
  const getResult = (obtained: string | number, minMarks: number) => {
    if (!obtained || obtained === "") return "-";

    // Use min marks directly from the API instead of calculating it
    return Number(obtained) >= minMarks ? (
      <span className="badge badge-soft-success d-inline-flex align-items-center">
        <i className="ti ti-circle-filled fs-5 me-1"></i>
        Pass
      </span>
    ) : (
      <span className="badge badge-soft-danger d-inline-flex align-items-center">
        <i className="ti ti-circle-filled fs-5 me-1"></i>
        Fail
      </span>
    );
  };

  const columns = [
    {
      title: "Student ID",
      dataIndex: "studentId",
      key: "studentIdDisplay",
      render: (text: string, record: any) => {
        const studentIdValue = record?.studentNumericId;

        if (studentIdValue) {
          return (
            <Link
              to={routes.studentResult.replace(
                ":id",
                studentIdValue.toString()
              )}
              className="link-primary"
            >
              {text}
            </Link>
          );
        }
        return text || "N/A";
      },
      sorter: (a: any, b: any) =>
        (a.studentId || "").localeCompare(b.studentId || ""),
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">{text}</p>
          </div>
        </div>
      ),
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
    },
    {
      title: "Course",
      key: "courseName",
      dataIndex: "courseName",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">{text}</p>
          </div>
        </div>
      ),
    },
    {
      title: "Stage",
      key: "stageName",
      dataIndex: "stageName",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">{text}</p>
          </div>
        </div>
      ),
    },
    {
      title: "Min/Max Marks",
      key: "minMaxMarks",
      render: (record: any) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              {record.min}/{record.max}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Marks",
      key: "marks",
      render: (record: any) => {
        const existingMark = doesMarkExist(record.id);
        return (
          <div
            className="d-flex align-items-center marks-input-container"
            style={styles.marksInputContainer}
          >
            <div className="input-group">
              <input
                type="number"
                className={`form-control ${
                  existingMark ? "border-warning" : "border-primary"
                }`}
                value={marks[record.id] || ""}
                onChange={(e) => handleMarkChange(record.id, e.target.value)}
                min="0"
                max={record.max}
                placeholder="Marks"
                style={{ height: "36px", borderRadius: "4px 0 0 4px" }}
              />
              <button
                className={`btn ${
                  existingMark ? "btn-warning" : "btn-primary"
                } d-flex align-items-center justify-content-center`}
                onClick={() => handleSaveIndividual(record.id)}
                disabled={loadingStudentId === record.id}
                style={{ width: "40px", height: "36px", padding: "0" }}
                title={existingMark ? "Update Mark" : "Save Mark"}
              >
                {loadingStudentId === record.id ? (
                  <i className="ti ti-loader animate-spin text-white" />
                ) : existingMark ? (
                  <i className="ti ti-pencil text-white" />
                ) : (
                  <i className="ti ti-device-floppy text-white" />
                )}
              </button>
            </div>
          </div>
        );
      },
    },
    {
      title: "Result",
      key: "result",
      render: (record: any) => getResult(marks[record.id], record.min),
    },
  ];

  // Add the getCheckboxProps function
  const getCheckboxProps = (record: any) => ({
    disabled: false, // Add your own logic here if needed
  });

  // Check if all filters are selected
  const areAllFiltersSelected = useMemo(() => {
    if (isSuperAdmin) {
      return (
        selectedSession && selectedCenterId && selectedCourseId && selectedLevel
      );
    }
    return selectedSession && selectedCourseId && selectedLevel;
  }, [
    isSuperAdmin,
    selectedSession,
    selectedCenterId,
    selectedCourseId,
    selectedLevel,
  ]);

  // Get missing filters message
  const getMissingFiltersMessage = () => {
    const missingFilters = [];
    if (!selectedSession) missingFilters.push("Session");
    if (isSuperAdmin && !selectedCenterId) missingFilters.push("Center");
    if (!selectedCourseId) missingFilters.push("Course");
    if (!selectedLevel) missingFilters.push("Level");
    return missingFilters.join(", ");
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isEdit ? "Edit" : "Assign"} Marks
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Assign"} Marks
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Marks Assignment</h4>
            </div>
            <div className="card-body">
              {/* Filters Section */}
              <div className="row mb-4">
                <div className={`col-md-${isSuperAdmin ? "3" : "4"} mb-3`}>
                  <label className="form-label">Select Session</label>
                  <select
                    className="form-select"
                    value={selectedSession}
                    onChange={handleSessionChange}
                    required
                    disabled={isLoadingSessions}
                  >
                    <option value="">Select Session</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
                {isSuperAdmin && (
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Select Center</label>
                    <select
                      className="form-select"
                      value={selectedCenterId}
                      onChange={handleCenterChange}
                      required
                      disabled={!selectedSession}
                    >
                      <option value="">Select Center</option>
                      {centers.map((center) => (
                        <option key={center.id} value={center.id}>
                          {center.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={`col-md-${isSuperAdmin ? "3" : "4"} mb-3`}>
                  <label className="form-label">Select Course</label>
                  <select
                    className="form-select"
                    value={selectedCourseId || ""}
                    onChange={handleCourseChange}
                    required
                    disabled={
                      isLoadingCourses ||
                      !selectedSession ||
                      (isSuperAdmin && !selectedCenterId)
                    }
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={`col-md-${isSuperAdmin ? "3" : "4"} mb-3`}>
                  <label className="form-label">Select Level</label>
                  <select
                    className="form-select"
                    value={selectedLevel}
                    onChange={handleLevelChange}
                    disabled={!selectedCourseId}
                    required
                  >
                    <option value="">Select Level</option>
                    {courseStages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bulk Assignment Section */}
              {areAllFiltersSelected && (
                <div
                  className="card bg-light-info border-0 mb-4"
                  style={styles.bulkAssignmentCard}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <h5 className="mb-1">Quick Assignment</h5>
                        <p className="mb-0 text-muted">
                          Assign same marks to selected students (
                          {selectedStudentIds.length} selected)
                        </p>
                      </div>
                      <div className="d-flex gap-2">
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter marks"
                          value={assignToSelectedMarks}
                          onChange={(e) =>
                            handleAssignToSelectedMarks(e.target.value)
                          }
                          style={{ width: "120px" }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAssignToSelected}
                          disabled={selectedStudentIds.length === 0}
                        >
                          <i className="ti ti-users me-1"></i>
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Table Section */}
              {areAllFiltersSelected ? (
                <div className="table-responsive">
                  {isLoadingStudents ? (
                    <div className="text-center">Loading student data...</div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={isLoadingStudents ? [] : tableData}
                      rowKey="id"
                      Selection={true}
                      onSelectionChange={handleSelectionChange}
                      selectedKeys={selectedStudentIds}
                      onSearchChange={handleSearch}
                      getCheckboxProps={getCheckboxProps}
                      pagination={{
                        current: currentPage,
                        pageSize: recordsPerPage,
                        total: paginationData.totalItems || 0,
                        onChange: handlePaginationChange,
                        pageSizeOptions: [10, 20, 30],
                      }}
                    />
                  )}
                  {isLoadingStudents && (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="ti ti-filter fs-1 text-muted mb-3"></i>
                  <h4>Please select all filters to view the list</h4>
                  <p className="text-warning">
                    {getMissingFiltersMessage()
                      ? `Missing filters: ${getMissingFiltersMessage()}`
                      : "Select all filters to see the student list"}
                  </p>
                </div>
              )}

              {/* Save All Button */}
              {areAllFiltersSelected && (
                <div className="text-end mt-4">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={handleSaveAll}
                    disabled={isAssigningBulk || isUpdatingBulk}
                  >
                    {isAssigningBulk || isUpdatingBulk ? (
                      <span className="spinner-border spinner-border-sm me-2" />
                    ) : (
                      <i className="ti ti-device-floppy me-2" />
                    )}
                    Save All Marks
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignMarks;
